import { BaseService } from './BaseService';
import type {
  Bot,
  Model,
  Workspace,
  AddFavouriteResponse,
} from '../types/api.types';
import type {
  CreateBotRequest,
  CreateBotResponse,
  CreateAssistantRequest,
  CreateAssistantResponse,
} from '../types/createBot.types';
import { API_ENDPOINTS } from '../config/api.config';

export class BotService extends BaseService {
  async getAllBots(): Promise<Bot[]> {
    const bots = await this.get<Bot[]>(API_ENDPOINTS.ALL_BOTS);
    return bots || [];
  }

  async getUserBots(uid: string): Promise<Bot[]> {
    const bots = await this.get<Bot[]>(API_ENDPOINTS.USER_BOTS(uid));
    return bots || [];
  }

  async getBotDetails(botId: string): Promise<Bot | null> {
    // Skip API call for preview/draft bots (they don't exist in the backend)
    if (botId.startsWith('preview-assistant')) {
      return null;
    }
    return null;
  }

  /**
   * Get enhanced bot details including RAG configuration and capabilities
   * This fetches additional information needed for chat interaction
   */
  async getEnhancedBotDetails(botId: string): Promise<Bot | null> {
    // Skip API call for preview/draft bots (they don't exist in the backend)
    if (botId.startsWith('preview-assistant')) {
      return null;
    }
    try {
      const bot = await this.getBotDetails(botId);
      if (!bot) {
        return null;
      }

      // Enhance the bot with additional properties for chat interaction
      const enhancedBot: Bot = {
        ...bot,
        // Ensure FAQs are available for welcome screen
        faqs: bot.faqs || [],
        // Ensure datastore is available for RAG
        datastore: bot.datastore || [],
        // Set default values for required fields if missing
        temperature: bot.temperature || 0.7,
        k_value: bot.k_value || 5,
        system_instructions:
          bot.system_instructions || 'You are a helpful assistant.',
      };

      // console.log('✨ [BotService] Enhanced bot ready');

      return enhancedBot;
    } catch (error) {
      console.error(
        '❌ [BotService] Failed to get enhanced bot details:',
        error
      );
      return null;
    }
  }

  /**
   * Get bot data stores for RAG functionality
   */
  async getBotDataStores(botId: string): Promise<string[]> {
    // Skip API call for preview/draft bots (they don't exist in the backend)
    if (botId.startsWith('preview-assistant')) {
      return [];
    }
    try {
      // First get the bot details to see what datastores it has
      const bot = await this.getBotDetails(botId);
      if (!bot || !bot.datastore) {
        return [];
      }

      // Return the datastore array
      return Array.isArray(bot.datastore) ? bot.datastore : [bot.datastore];
    } catch (error) {
      console.error('Failed to get bot data stores:', error);
      return [];
    }
  }

  async getAllModels(): Promise<Model[]> {
    const models = await this.get<Model[]>(API_ENDPOINTS.ALL_MODELS);
    return models || [];
  }

  // Assistant Hub methods

  async getWorkspacesList(): Promise<Workspace[]> {
    const workspaceNames = await this.get<string[]>(
      API_ENDPOINTS.WORKSPACES_LIST
    );
    if (workspaceNames) {
      // Convert string array to Workspace objects
      return workspaceNames.map(name => ({
        id: name,
        name: name,
      }));
    }
    return [];
  }

  async getWorkspaceBots(workspaceName: string): Promise<Bot[]> {
    const bots = await this.get<Bot[]>(
      API_ENDPOINTS.WORKSPACE_BOTS(workspaceName)
    );
    return bots || [];
  }

  async addBotToFavourites(
    uid: string,
    botId: string
  ): Promise<AddFavouriteResponse | null> {
    const response = await this.post<AddFavouriteResponse>(
      API_ENDPOINTS.ADD_FAVORITE(uid, botId),
      {} // Empty body as the data is in the URL query params
    );
    return response || null;
  }

  async removeBotFromFavourites(
    uid: string,
    botId: string
  ): Promise<{ message: string } | null> {
    const response = await this.post<{ message: string }>(
      API_ENDPOINTS.REMOVE_FAVORITE(uid, botId),
      {} // Empty body as the data is in the URL query params
    );
    return response || null;
  }

  async createBot(data: CreateBotRequest): Promise<CreateBotResponse> {
    const response = await this.post<CreateBotResponse>(
      API_ENDPOINTS.BOT_CREATE,
      data
    );

    if (!response) {
      throw new Error('Failed to create bot');
    }

    return response;
  }

  async updateBot(
    botId: string,
    data: Partial<CreateBotRequest>
  ): Promise<Bot> {
    const response = await this.patch<Bot>(
      API_ENDPOINTS.BOT_UPDATE(botId),
      data
    );

    if (!response) {
      throw new Error('Failed to update bot');
    }

    return response;
  }

  async createAssistant(
    data: CreateAssistantRequest
  ): Promise<CreateAssistantResponse> {
    const response = await this.post<CreateAssistantResponse>(
      API_ENDPOINTS.ASSISTANT_CREATE,
      data
    );

    if (!response) {
      throw new Error('Failed to create assistant');
    }

    return response;
  }

  async updateAssistant(
    assistantUid: string,
    data: CreateAssistantRequest
  ): Promise<CreateAssistantResponse> {
    const response = await this.put<CreateAssistantResponse>(
      API_ENDPOINTS.ASSISTANT_UPDATE(assistantUid),
      data
    );

    if (!response) {
      throw new Error('Failed to update assistant');
    }

    return response;
  }
}

// Lazy initialization to avoid circular dependency issues
let _botServiceInstance: BotService | null = null;

function getBotServiceInstance(): BotService {
  if (!_botServiceInstance) {
    _botServiceInstance = new BotService();
  }
  return _botServiceInstance;
}

// Export a proxy that lazily initializes the service
export const botService = new Proxy({} as BotService, {
  get(_target, prop) {
    const instance = getBotServiceInstance();
    const value = instance[prop as keyof BotService];
    // Bind methods to the instance
    return typeof value === 'function' ? value.bind(instance) : value;
  },
});
